import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Briefcase, Inbox, TrendingUp, MessageSquare, MapPin, Star, Award,
  Search, ChevronRight, ArrowUpRight, Phone, Mail, FileText, CheckCircle2, X, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ------------------------------------------------------------------ */
/*  Recruiter Pipeline — deep dashboard for recruiter role            */
/*  Tabs: Pipeline (clinicians) · Submissions · Leads                  */
/* ------------------------------------------------------------------ */

const TABS = [
  { key: "pipeline",    label: "Talent Pipeline",  Icon: Users },
  { key: "submissions", label: "Submissions",      Icon: Briefcase },
  { key: "leads",       label: "Lead Inbox",       Icon: Inbox },
];

const APP_STATUS_PIPE = ["submitted", "in_review", "interview", "hired", "rejected"];

const STATUS_STYLES = {
  submitted:  "bg-amber-50 text-amber-700 border-amber-200",
  in_review:  "bg-trital-medical-blue-soft text-trital-navy border-trital-navy/20",
  interview:  "bg-blue-50 text-blue-700 border-blue-200",
  hired:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected:   "bg-red-50 text-trital-red border-red-200",
  new:        "bg-amber-50 text-amber-700 border-amber-200",
  contacted:  "bg-blue-50 text-blue-700 border-blue-200",
  qualified:  "bg-trital-medical-blue-soft text-trital-navy border-trital-navy/20",
  closed:     "bg-slate-100 text-slate-700 border-slate-200",
};

export default function RecruiterPipeline() {
  const [tab, setTab] = useState("pipeline");
  const [clinicians, setClinicians] = useState([]);
  const [apps, setApps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [appFilter, setAppFilter] = useState("all");
  const [leadFilter, setLeadFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a, l] = await Promise.all([
        api.get("/clinicians"),
        api.get("/applications/mine"),
        api.get("/admin/leads"),
      ]);
      setClinicians(c.data || []);
      setApps(a.data || []);
      setLeads(l.data || []);
    } catch (e) {
      toast.error("Couldn't load pipeline", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const kpis = useMemo(() => {
    const pendingApps = apps.filter((a) => ["submitted", "in_review"].includes(a.status)).length;
    const interviews  = apps.filter((a) => a.status === "interview").length;
    const newLeads    = leads.filter((l) => l.status === "new").length;
    return [
      { Icon: Users,      label: "Active clinicians",    value: clinicians.length, color: "navy" },
      { Icon: Briefcase,  label: "Submissions to review",value: pendingApps,       color: "red" },
      { Icon: TrendingUp, label: "In interview",         value: interviews,        color: "blue" },
      { Icon: Inbox,      label: "Fresh leads",          value: newLeads,          color: "green" },
    ];
  }, [clinicians, apps, leads]);

  const filteredClinicians = useMemo(() => {
    if (!q) return clinicians;
    const ql = q.toLowerCase();
    return clinicians.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(ql) ||
        (c.specialty || "").toLowerCase().includes(ql) ||
        (c.license_state || "").toLowerCase().includes(ql)
    );
  }, [q, clinicians]);

  const filteredApps  = useMemo(
    () => (appFilter === "all"  ? apps  : apps.filter((a) => a.status === appFilter)),
    [appFilter, apps],
  );
  const filteredLeads = useMemo(
    () => (leadFilter === "all" ? leads : leads.filter((l) => l.status === leadFilter)),
    [leadFilter, leads],
  );

  /* Actions */
  const setAppStatus = async (id, action) => {
    try {
      await api.post(`/applications/${id}/action`, { action });
      toast.success(`Application moved to ${action}`);
      load();
    } catch (e) {
      toast.error("Action failed", { description: e?.response?.data?.detail || e.message });
    }
  };
  const setLeadStatus = async (id, status) => {
    try {
      await api.post(`/admin/leads/${id}/status`, { status });
      toast.success(`Lead → ${status}`);
      load();
    } catch (e) {
      toast.error("Couldn't update lead", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-6" data-testid="recruiter-pipeline">
      <header>
        <div className="overline text-trital-red">Recruiter desk</div>
        <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">Talent Pipeline</h1>
        <p className="text-slate-600 text-sm mt-1">Move clinicians from interest → interview → hired. Inbox new leads. Track every submission.</p>
      </header>

      {/* KPI cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => <KpiCard key={k.label} {...k} index={i} />)}
      </div>

      {/* Tabs */}
      <nav className="flex gap-1 border-b border-slate-200" role="tablist" data-testid="recruiter-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-[13px] font-semibold transition-colors ${
                active ? "border-trital-red text-trital-navy" : "border-transparent text-slate-600 hover:text-trital-navy"
              }`}
              data-testid={`recruiter-tab-${t.key}`}
            >
              <t.Icon className="w-4 h-4" strokeWidth={1.8} /> {t.label}
            </button>
          );
        })}
      </nav>

      {/* Tab content */}
      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="recruiter-loading">Loading pipeline…</div>
      ) : tab === "pipeline" ? (
        <PipelineTab clinicians={filteredClinicians} q={q} setQ={setQ} apps={apps} />
      ) : tab === "submissions" ? (
        <SubmissionsTab apps={filteredApps} filter={appFilter} setFilter={setAppFilter} onAction={setAppStatus} />
      ) : (
        <LeadsTab leads={filteredLeads} filter={leadFilter} setFilter={setLeadFilter} onStatus={setLeadStatus} />
      )}
    </div>
  );
}

/* ==================================================================
 *  Sub-components
 * ================================================================== */

function KpiCard({ Icon, label, value, color, index }) {
  const palette = {
    navy:  "bg-trital-medical-blue-soft text-trital-navy",
    blue:  "bg-blue-50 text-blue-700",
    red:   "bg-red-50 text-trital-red",
    green: "bg-emerald-50 text-emerald-700",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card"
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${palette[color]}`}><Icon className="w-5 h-5" strokeWidth={1.8} /></div>
      <div className="mt-3 font-display font-black text-3xl text-slate-900 tabular-nums">{value}</div>
      <div className="overline text-slate-500 mt-1">{label}</div>
    </motion.div>
  );
}

function PipelineTab({ clinicians, q, setQ, apps }) {
  return (
    <div className="space-y-4" data-testid="pipeline-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.8} />
          <input
            type="search"
            placeholder="Search by name, specialty, state"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13.5px] focus:outline-none focus:border-trital-navy"
            data-testid="pipeline-search"
          />
        </div>
        <span className="text-[12px] text-slate-500">{clinicians.length} clinicians</span>
      </div>
      {clinicians.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No clinicians match.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clinicians.map((c) => {
            const myApps = apps.filter((a) => a.clinician_id === c.user_id);
            return (
              <motion.div
                key={c.id || c.user_id}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.25 }}
                className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all"
                data-testid={`pipeline-card-${c.user_id || c.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-trital-navy text-white flex items-center justify-center font-display font-bold">{(c.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-slate-900 truncate">{c.name}</div>
                    <div className="text-[12px] text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.8} /> {c.license_state || "—"} · {c.years_experience || 0} yrs</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{c.specialty}</span>
                  {c.travel_ready && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold uppercase tracking-wider">Travel</span>}
                  {myApps.length > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-red/10 text-trital-red font-bold uppercase tracking-wider">{myApps.length} active</span>}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-[12px]">
                  <div className="inline-flex items-center gap-1 text-slate-700"><Award className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />{c.pay_expectation ? `$${c.pay_expectation}/hr` : "—"}</div>
                  <Link to="/dashboard/messages" className="inline-flex items-center gap-1 text-trital-navy font-semibold hover:gap-1.5 transition-all">
                    Message <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SubmissionsTab({ apps, filter, setFilter, onAction }) {
  return (
    <div className="space-y-4" data-testid="submissions-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {["all", ...APP_STATUS_PIPE].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              filter === s ? "bg-trital-navy text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-trital-navy"
            }`}
            data-testid={`submissions-filter-${s}`}
          >
            {s === "all" ? "All" : s.replace("_", " ")}
          </button>
        ))}
        <span className="ml-auto text-[12px] text-slate-500">{apps.length} submissions</span>
      </div>

      {apps.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No submissions match this filter.</div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Clinician</th>
                <th className="text-left px-5 py-3">Job</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Submitted</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apps.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50" data-testid={`app-row-${a.id}`}>
                  <td className="px-5 py-3 font-semibold text-slate-900">{a.clinician_name || a.clinician_id}</td>
                  <td className="px-5 py-3 text-slate-600">
                    <div>{a.job_title}</div>
                    <div className="text-[11px] text-slate-400">{a.facility_name}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[a.status] || "bg-slate-100 text-slate-700"}`}>{(a.status || "submitted").replace("_", " ")}</span>
                  </td>
                  <td className="px-5 py-3 text-[12px] text-slate-500">{a.created_at?.slice(0, 10)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1.5 justify-end">
                      {a.status !== "in_review" && a.status !== "interview" && a.status !== "hired" && a.status !== "rejected" && (
                        <button onClick={() => onAction(a.id, "review")} className="px-2.5 py-1 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[11px] font-bold hover:bg-trital-navy hover:text-white" data-testid={`app-review-${a.id}`}>Review</button>
                      )}
                      {a.status !== "interview" && a.status !== "hired" && a.status !== "rejected" && (
                        <button onClick={() => onAction(a.id, "interview")} className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-bold hover:bg-blue-700 hover:text-white" data-testid={`app-interview-${a.id}`}>Interview</button>
                      )}
                      {a.status !== "hired" && a.status !== "rejected" && (
                        <button onClick={() => onAction(a.id, "hired")} className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold hover:bg-emerald-700 hover:text-white" data-testid={`app-hire-${a.id}`}>Hire</button>
                      )}
                      {a.status !== "rejected" && a.status !== "hired" && (
                        <button onClick={() => onAction(a.id, "rejected")} className="px-2.5 py-1 rounded-full bg-red-50 text-trital-red text-[11px] font-bold hover:bg-trital-red hover:text-white" data-testid={`app-reject-${a.id}`}>Reject</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LeadsTab({ leads, filter, setFilter, onStatus }) {
  return (
    <div className="space-y-4" data-testid="leads-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {["all", "new", "contacted", "qualified", "closed"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              filter === s ? "bg-trital-navy text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-trital-navy"
            }`}
            data-testid={`leads-filter-${s}`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
        <span className="ml-auto text-[12px] text-slate-500">{leads.length} leads</span>
      </div>

      {leads.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No leads match.</div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <motion.div
              key={l.id}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25 }}
              className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card grid lg:grid-cols-[1.4fr_1fr_auto] gap-4 items-start"
              data-testid={`lead-row-${l.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[l.status] || "bg-slate-100 text-slate-700"}`}>{l.status || "new"}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{l.source || "other"}</span>
                  <span className="text-[11px] text-slate-400">· {l.created_at?.slice(0, 10)}</span>
                </div>
                <div className="mt-2 font-display font-bold text-slate-900">{l.name || "—"}</div>
                {l.organization && <div className="text-[12.5px] text-slate-600">{l.organization}</div>}
                {l.notes && <p className="mt-1.5 text-[12.5px] text-slate-500 line-clamp-2">{l.notes}</p>}
              </div>
              <div className="space-y-1.5">
                {l.email && (
                  <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-700 hover:text-trital-navy"><Mail className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> {l.email}</a>
                )}
                {l.phone && (
                  <a href={`tel:${l.phone}`} className="block inline-flex items-center gap-1.5 text-[12.5px] text-slate-700 hover:text-trital-navy"><Phone className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> {l.phone}</a>
                )}
              </div>
              <div className="flex flex-col gap-1.5 lg:items-end">
                <select
                  value={l.status || "new"}
                  onChange={(e) => onStatus(l.id, e.target.value)}
                  className="px-3 py-1.5 rounded-full text-[12px] font-semibold border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-trital-navy"
                  data-testid={`lead-status-${l.id}`}
                >
                  <option value="new">new</option>
                  <option value="contacted">contacted</option>
                  <option value="qualified">qualified</option>
                  <option value="closed">closed</option>
                </select>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
